// frontend/src/pages/shops/BusinessOrders.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SidebarLayout from './SidebarLayout';
import SellerReturnsPanel from '../../components/returns/SellerReturnsPanel';
import '../staff/Orders.css';

function BusinessOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeView, setActiveView] = useState('orders');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [initialReturnCaseId, setInitialReturnCaseId] = useState('');
  const [markingPickup, setMarkingPickup] = useState(false);
  const activeRole = localStorage.getItem('ogestyle_role_override') || localStorage.getItem('ogestyle_role');
  const isR2View = activeRole === 'R2';

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('ogestyle_token');
    if (!token) {
      navigate('/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setError('Configuration error. Please contact support.');
      return null;
    }
    return baseURL;
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      setLoading(true);
      const res = await axios.get(`${baseURL}/api/business/orders`, { headers });
      setOrders(res.data.orders || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleMarkPickedUp = async (orderId, sellerOrderId = null, shopId = null) => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      setMarkingPickup(true);
      await axios.put(
        `${baseURL}/api/business/orders/${orderId}/pickup`,
        { sellerOrderId, shopId },
        { headers },
      );

      await fetchOrders();
      setSelectedOrder(null);
      setMarkingPickup(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to mark order as picked up');
      setMarkingPickup(false);
    }
  };

  const hasReturnActivity = (order) => {
    const summary = order?.returnSummary || {};
    return (
      String(summary.derivedReturnStatus || 'none') !== 'none' ||
      Number(summary.returnLinkedItemCount || 0) > 0 ||
      (Array.isArray(summary.activeReturnCaseIds) && summary.activeReturnCaseIds.length > 0)
    );
  };

  const getEffectivePickupStatus = (order) => {
    if (hasReturnActivity(order)) {
      return order?.returnSummary?.derivedReturnStatus || 'return_initiated';
    }
    return order?.pickupStatus || 'pending';
  };

  const openReturnCaseForOrder = (order) => {
    const caseId = order?.returnSummary?.primaryReturnCaseId;
    if (!caseId) return;
    setInitialReturnCaseId(caseId);
    setSelectedOrder(null);
    setActiveView('returns');
  };

  const filteredOrders = orders.filter((order) => {
    if (filter === 'pending' && (hasReturnActivity(order) || order.pickupStatus === 'picked_up')) return false;
    if (filter === 'picked_up' && order.pickupStatus !== 'picked_up') return false;

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesOrderNumber = order.orderNumber?.toLowerCase().includes(search);
      const matchesCustomer = `${order.customerName?.firstName} ${order.customerName?.lastName}`
        .toLowerCase()
        .includes(search);
      const matchesEmail = order.customerEmail?.toLowerCase().includes(search);

      if (!matchesOrderNumber && !matchesCustomer && !matchesEmail) return false;
    }

    return true;
  });

  const pendingCount = orders.filter((o) => !hasReturnActivity(o) && o.pickupStatus !== 'picked_up').length;
  const pickedUpCount = orders.filter((o) => o.pickupStatus === 'picked_up').length;

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getStatusKey = (status) => String(status || 'pending').toLowerCase();

  const getDetailStatusLabel = (status) => getStatusKey(status).replace('_', ' ');

  const getDetailStatusClass = (status) => {
    const statusKey = getStatusKey(status);
    if (statusKey === 'pending') return 'detail-status-pill detail-status-pill--pending';
    if (statusKey === 'picked_up' || statusKey === 'fulfilled') return 'detail-status-pill detail-status-pill--picked';
    if (statusKey === 'cancelled') return 'detail-status-pill detail-status-pill--cancelled';
    return 'detail-status-pill detail-status-pill--neutral';
  };

  const renderLocationSlices = (order) => {
    if (!order.shops?.length) return null;

    return (
      <div className="business-owner-location-grid">
        {order.shops.map((shop, idx) => {
          const sliceStatus = shop.pickupStatus || order.pickupStatus || 'pending';
          const isPickedUp = sliceStatus === 'picked_up';
          const firstItem = (shop.items || [])[0]?.name;
          const firstBooking = (shop.bookings || [])[0]?.serviceName;
          const itemLabel = firstItem || firstBooking || 'Item';
          const shopId = shop.shopId?._id || shop.shopId || null;
          const city = shop.city || shop.shopId?.city || '';
          const state = shop.state || shop.shopId?.state || '';
          const cityState = [city, state].filter(Boolean).join(', ') || 'City, State';

          return (
            <div key={`${order._id}-${shopId || idx}`} className="business-owner-location-card">
              <div className="business-owner-location-info">
                <div className="business-owner-location-name">{shop.shopName || shop.shopId?.name || 'Location'}</div>
                <div className="business-owner-location-city">{cityState}</div>
              </div>

              <div className="business-owner-location-item">{itemLabel}</div>

              <div className="business-owner-location-action">
                {shop.sellerOrderId ? (
                  <button
                    className="btn-pickup business-owner-location-pickup"
                    onClick={() => handleMarkPickedUp(order._id, shop.sellerOrderId, shopId)}
                    disabled={markingPickup || isPickedUp}
                  >
                    {markingPickup ? '...' : 'Pick Up'}
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderOrdersView = () => (
    <>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="orders-filters">
        <div className="filter-tabs">
          <button className={`filter-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
            All Orders <span className="tab-count">{orders.length}</span>
          </button>
          <button
            className={`filter-tab ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pending Pickup <span className="tab-count">{pendingCount}</span>
          </button>
          <button
            className={`filter-tab ${filter === 'picked_up' ? 'active' : ''}`}
            onClick={() => setFilter('picked_up')}
          >
            Picked Up <span className="tab-count">{pickedUpCount}</span>
          </button>
        </div>

        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Search by order #, customer name, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📦</div>
          <h3>No orders found</h3>
          <p>
            {filter === 'pending'
              ? 'All orders have been picked up!'
              : filter === 'picked_up'
                ? 'No completed orders yet.'
                : "You haven't received any orders yet."}
          </p>
        </div>
      ) : isR2View ? (
        <div className="orders-list freelancer-orders-grid">
          <div className="freelancer-orders-header-row">
            <div>ORDER NUMBER</div>
            <div>CUSTOMER NAME</div>
            <div>ITEM</div>
            <div>PRICE</div>
            <div>ACTION BUTTONS</div>
          </div>

          {filteredOrders.map((order) => {
            const firstItem = order.shops?.flatMap((shop) => shop.items || [])[0];
            const firstBooking = order.shops?.flatMap((shop) => shop.bookings || [])[0];
            const itemLabel = firstItem?.name || firstBooking?.serviceName || 'Item unavailable';
            const statusKey = getEffectivePickupStatus(order).toLowerCase();
            const statusLabel = statusKey.replace('_', ' ');
            const isActionable = !hasReturnActivity(order) && !['picked_up', 'fulfilled', 'cancelled'].includes(statusKey);

            return (
              <div key={order._id} className="order-card freelancer-orders-row-card">
                <div className="freelancer-orders-row">
                  <div className="freelancer-col-order">
                    <div className="freelancer-order-number">#{order.orderNumber}</div>
                    <div className="freelancer-order-date">{formatDate(order.createdAt)}</div>
                  </div>

                  <div className="freelancer-col-customer">
                    {order.customerName?.firstName} {order.customerName?.lastName}
                  </div>

                  <div className="freelancer-col-item">{itemLabel}</div>

                  <div className="freelancer-col-price">${order.totalAmount?.toFixed(2)}</div>

                  <div className="freelancer-col-actions">
                    <button className="btn-view freelancer-btn-view" onClick={() => setSelectedOrder(order)}>
                      View
                    </button>
                    <button
                      className={`freelancer-status-pill freelancer-status-pill--${statusKey}`}
                      onClick={() => {
                        if (isActionable) handleMarkPickedUp(order._id);
                      }}
                      disabled={markingPickup || !isActionable}
                    >
                      {markingPickup && isActionable ? '...' : statusLabel}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="orders-list business-owner-orders-grid">
          <div className="business-owner-header-row">
            <div className="business-owner-head-order">ORDER NUMBER</div>
            <div className="business-owner-head-customer">CUSTOMER NAME</div>
            <div className="business-owner-head-price">PRICE</div>
            <div className="business-owner-head-actions">ACTION BUTTONS</div>
          </div>

          {filteredOrders.map((order) => {
            const statusKey = getEffectivePickupStatus(order).toLowerCase();
            const statusLabel = statusKey.replace('_', ' ');

            return (
              <div key={order._id} className="business-owner-order-block">
                <div className="order-card business-owner-row-card">
                  <div className="business-owner-row">
                    <div className="business-owner-col-order">
                      <div className="business-owner-order-number">#{order.orderNumber}</div>
                      <div className="business-owner-order-date">{formatDate(order.createdAt)}</div>
                    </div>

                    <div className="business-owner-col-customer">
                      {order.customerName?.firstName} {order.customerName?.lastName}
                    </div>

                    <div className="business-owner-col-price">${order.totalAmount?.toFixed(2)}</div>

                    <div className="business-owner-col-actions">
                      <button className="btn-view" onClick={() => setSelectedOrder(order)}>
                        View
                      </button>
                      <span className={`business-owner-status-pill business-owner-status-pill--${statusKey}`}>{statusLabel}</span>
                    </div>
                  </div>

                  <div className="business-owner-location-section">{renderLocationSlices(order)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  if (loading) {
    return (
      <SidebarLayout>
        <div className="orders-container">
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280', fontSize: '13px' }}>
            Loading orders...
          </div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="orders-container">
        <div className="orders-header">
          <h1>📦 Product Orders</h1>
          <p className="orders-subtitle">Manage customer orders and pickup status</p>
        </div>

        <div className="filter-tabs" style={{ marginBottom: '12px' }}>
          <button
            className={`filter-tab ${activeView === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveView('orders')}
          >
            Orders
          </button>
          <button
            className={`filter-tab ${activeView === 'returns' ? 'active' : ''}`}
            onClick={() => setActiveView('returns')}
          >
            Returns
          </button>
        </div>

        {activeView === 'returns' ? (
          <SellerReturnsPanel
            getAuthHeaders={getAuthHeaders}
            getApiBaseUrl={getApiBaseUrl}
            enforcedOwnerRoute="R1"
            ownerRouteLabel="Business"
            initialCaseId={initialReturnCaseId}
            onInitialCaseOpened={() => setInitialReturnCaseId('')}
          />
        ) : (
          renderOrdersView()
        )}

        {activeView === 'orders' && selectedOrder && (
          <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
            <div className={`modal-content ${!isR2View ? 'modal-content-r1' : ''}`} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Order Details</h2>
                <button className="modal-close" onClick={() => setSelectedOrder(null)}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="order-detail-section">
                  <div className="order-detail-meta-grid">
                    <div className="order-detail-meta-row">
                      <span className="order-detail-meta-label">Order Number</span>
                      <span className="order-detail-meta-value">{selectedOrder.orderNumber}</span>
                    </div>
                    <div className="order-detail-meta-row">
                      <span className="order-detail-meta-label">Order Date</span>
                      <span className="order-detail-meta-value">{formatDate(selectedOrder.createdAt)}</span>
                    </div>
                    <div className="order-detail-meta-row">
                      <span className="order-detail-meta-label">Status</span>
                      <span className={getDetailStatusClass(getEffectivePickupStatus(selectedOrder))}>
                        {getDetailStatusLabel(getEffectivePickupStatus(selectedOrder))}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="order-detail-section">
                  <h3 className="order-detail-section-title">Customer</h3>
                  <div className="order-detail-meta-grid">
                    <div className="order-detail-meta-row">
                      <span className="order-detail-meta-label">Name</span>
                      <span className="order-detail-meta-value">
                        {selectedOrder.customerName?.firstName} {selectedOrder.customerName?.lastName}
                      </span>
                    </div>
                    <div className="order-detail-meta-row">
                      <span className="order-detail-meta-label">Email</span>
                      <span className="order-detail-meta-value">{selectedOrder.customerEmail}</span>
                    </div>
                    {selectedOrder.customerPhone && (
                      <div className="order-detail-meta-row">
                        <span className="order-detail-meta-label">Phone</span>
                        <span className="order-detail-meta-value">{selectedOrder.customerPhone}</span>
                      </div>
                    )}
                    {selectedOrder.billingAddress && (
                      <div className="order-detail-meta-row order-detail-meta-row--full">
                        <span className="order-detail-meta-label">Billing Address</span>
                        <span className="order-detail-meta-value">
                          {selectedOrder.billingAddress.street}, {selectedOrder.billingAddress.city}, {selectedOrder.billingAddress.state}{' '}
                          {selectedOrder.billingAddress.zipCode}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {isR2View && (
                  <div className="order-detail-section">
                    <h3 className="order-detail-section-title">Items</h3>
                    {selectedOrder.shops?.flatMap((shop) => shop.items || []).map((item, itemIdx) => (
                      <div key={`r2-item-${itemIdx}`} className="order-detail-line-item">
                        {item.name} x {item.quantity} = ${(item.priceAtPurchase * item.quantity).toFixed(2)}
                      </div>
                    ))}
                    {selectedOrder.shops?.flatMap((shop) => shop.bookings || []).map((booking, bookingIdx) => (
                      <div key={`r2-booking-${bookingIdx}`} className="order-detail-line-item">
                        {booking.serviceName} - ${booking.servicePrice?.toFixed(2)}
                      </div>
                    ))}
                    {!selectedOrder.shops?.some(
                      (shop) => (shop.items && shop.items.length > 0) || (shop.bookings && shop.bookings.length > 0),
                    ) && <div className="order-detail-line-item">No item details available.</div>}
                  </div>
                )}

                {!isR2View && (
                  <div className="order-detail-section">
                    <h3 className="order-detail-section-title">Locations</h3>
                    <div className="order-detail-location-list order-detail-location-list--r1">
                      {selectedOrder.shops?.map((shop, idx) => {
                        const sliceStatus = hasReturnActivity(selectedOrder)
                          ? getEffectivePickupStatus(selectedOrder)
                          : shop.pickupStatus || selectedOrder.pickupStatus || 'pending';
                        const city = shop.city || shop.shopId?.city || '';
                        const state = shop.state || shop.shopId?.state || '';
                        const cityState = [city, state].filter(Boolean).join(', ');

                        return (
                          <div key={idx} className="order-detail-location-card">
                            <div className="order-detail-location-head">
                              <h4>{shop.shopName || shop.shopId?.name || 'Location'}</h4>
                              <span className={getDetailStatusClass(sliceStatus)}>{getDetailStatusLabel(sliceStatus)}</span>
                            </div>

                            <div className="order-detail-location-address">{cityState || 'City, State unavailable'}</div>

                            {shop.items?.map((item, itemIdx) => (
                              <div key={itemIdx} className="order-detail-line-item">
                                {item.name} x {item.quantity} = ${(item.priceAtPurchase * item.quantity).toFixed(2)}
                              </div>
                            ))}

                            {shop.bookings?.map((booking, bookingIdx) => (
                              <div key={`booking-${bookingIdx}`} className="order-detail-line-item">
                                {booking.serviceName} - ${booking.servicePrice?.toFixed(2)}
                              </div>
                            ))}

                            {shop.sellerOrderId && !hasReturnActivity(selectedOrder) && sliceStatus !== 'picked_up' && (
                              <div className="order-detail-location-actions">
                                <button
                                  className="btn-mark-pickup order-detail-pickup-btn"
                                  onClick={() =>
                                    handleMarkPickedUp(selectedOrder._id, shop.sellerOrderId, shop.shopId?._id || shop.shopId)
                                  }
                                  disabled={markingPickup}
                                >
                                  {markingPickup ? 'Marking...' : 'Pick Up'}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="order-detail-section order-detail-section--totals">
                  <div className="order-detail-meta-grid">
                    <div className="order-detail-meta-row">
                      <span className="order-detail-meta-label">Subtotal</span>
                      <span className="order-detail-meta-value">${selectedOrder.subtotal?.toFixed(2)}</span>
                    </div>
                    {selectedOrder.taxAmount > 0 && (
                      <div className="order-detail-meta-row">
                        <span className="order-detail-meta-label">Tax ({selectedOrder.taxRate?.toFixed(2)}%)</span>
                        <span className="order-detail-meta-value">${selectedOrder.taxAmount?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="order-detail-meta-row order-detail-total-row">
                      <span className="order-detail-meta-label">Total</span>
                      <span className="order-detail-meta-value">${selectedOrder.totalAmount?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                {selectedOrder.returnSummary?.primaryReturnCaseId && (
                  <button className="btn-view" onClick={() => openReturnCaseForOrder(selectedOrder)}>
                    Open Return Case
                  </button>
                )}
                {!hasReturnActivity(selectedOrder) && selectedOrder.pickupStatus !== 'picked_up' && (
                  <button
                    className="btn-mark-pickup"
                    onClick={() => handleMarkPickedUp(selectedOrder._id)}
                    disabled={markingPickup}
                  >
                    {markingPickup ? 'Marking...' : '✅ Mark as Picked Up'}
                  </button>
                )}
                <button className="btn-secondary" onClick={() => setSelectedOrder(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
}

export default BusinessOrders;
